import { z } from 'zod'
import { HOME_DELIVERY_GUIDE_PAGE_PATH } from 'utils/constants'

/** Every section of the guide, in page order. Also the `section` URL param's accepted values. */
export const HOME_DELIVERY_GUIDE_SECTION_IDS = [
  'how-to-prescribe',
  'how-to-change',
  'cost',
  'arrival',
  'sooner',
  'signatures',
  'pharmacy',
  'address',
  'medications',
  'after-the-visit',
] as const

export type HomeDeliveryGuideSectionId = (typeof HOME_DELIVERY_GUIDE_SECTION_IDS)[number]

/**
 * QMD-4967: where the guide was opened from, sent as `EntrySource` on its events. The nudges and the
 * card's `?` set it on their links; anything else (an announcement, a typed or shared URL) is `direct`.
 */
export const HOME_DELIVERY_GUIDE_ENTRY_SOURCES = [
  'home_delivery_requested',
  'pickup_mismatch',
  'help_icon',
  'direct',
] as const

export type HomeDeliveryGuideEntrySource = (typeof HOME_DELIVERY_GUIDE_ENTRY_SOURCES)[number]

const SECTION_PARAM = 'section'
const FROM_PARAM = 'from'

// A hand-edited or stale link opens the guide at the top rather than with nothing open.
const SectionParam = z.enum(HOME_DELIVERY_GUIDE_SECTION_IDS).catch('how-to-prescribe')

const FromParam = z.enum(HOME_DELIVERY_GUIDE_ENTRY_SOURCES).catch('direct')

/** The section the guide opens with, from the page's own URL. */
export const parseHomeDeliveryGuideSection = (params: URLSearchParams): HomeDeliveryGuideSectionId =>
  SectionParam.parse(params.get(SECTION_PARAM))

/** Where the guide was opened from, from the page's own URL. */
export const parseHomeDeliveryGuideEntrySource = (params: URLSearchParams): HomeDeliveryGuideEntrySource =>
  FromParam.parse(params.get(FROM_PARAM))

type HrefOptions = {
  /** The section to open; the first section when omitted. */
  section?: HomeDeliveryGuideSectionId | undefined
  from?: HomeDeliveryGuideEntrySource
}

/** Link to the guide, opening `section` and recording where it was opened `from`. */
export const homeDeliveryGuideHref = ({ section, from }: HrefOptions = {}): string => {
  const params = new URLSearchParams()
  if (section) params.set(SECTION_PARAM, section)
  if (from) params.set(FROM_PARAM, from)
  const query = params.toString()
  return query ? `${HOME_DELIVERY_GUIDE_PAGE_PATH}?${query}` : HOME_DELIVERY_GUIDE_PAGE_PATH
}
